import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { LoadingButton } from "@mui/lab";
import { Box, Chip } from "@mui/material";
import {
  FileInput,
  required,
  SelectArrayInput,
  SelectArrayInputClasses,
  SelectInput,
  TextInput,
  useInput,
  useRecordContext,
} from "react-admin";
import { type FieldValues, useFormContext } from "react-hook-form";
import { useState } from "react";
import { CreateDrivingLicenseAuthorityFormDto, getAuthenticatedServerClient, schemas } from "../../api/server.api";
import { UrlInput } from "../../inputs/UrlInput";
import { Column } from "../../misc/Column";
import { Form2 } from "../../misc/Form2";
import { Row } from "../../misc/Row";
import { formatFileSize } from "../../utils/string";
import { EditDrivingLicenseAuthorityFormTemplateButton } from "./EditDrivingLicenseAuthorityFormTemplateButton";
import { DrivingLicenseAuthorityForm } from "../../providers/drivingLicenseAuthorityFormsProvider";
import { SaveButton2 } from "../../buttons/SaveButton2";

export function DrivingLicenseAuthorityFormForm({ mode }: { mode: "create" | "edit" }) {
  const record = useRecordContext<DrivingLicenseAuthorityForm>();
  function validate(values: FieldValues) {
    const errors: Partial<CreateDrivingLicenseAuthorityFormDto> = {};
    if (!values.url && !values.pdf) {
      errors.url = "Bitte gib entweder einen Link zum Formular an...";
      errors.pdf = "...oder lade das Formular hoch.";
    }
    return errors;
  }
  return (
    <Form2 validate={validate}>
      <Column sx={{ alignItems: "stretch" }}>
        <_FormInputFields record={record} />
        <Row sx={{ justifyContent: "space-between" }} gap={2}>
          <SaveButton2 />
          {mode === "edit" && record?.pdfTemplateBaseFile && (
            <Row gap={1}>
              <DownloadDrivingLicenseAuthorityFormPDFButton file={record.pdfTemplateBaseFile} />
              <EditDrivingLicenseAuthorityFormTemplateButton />
              {/* TODO: <DeleteRecordButton size="medium" /> */}
            </Row>
          )}
        </Row>
      </Column>
    </Form2>
  );
}

function DownloadDrivingLicenseAuthorityFormPDFButton({
  file,
}: {
  file: NonNullable<DrivingLicenseAuthorityForm["pdfTemplateBaseFile"]>;
}) {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadForm = async () => {
    setIsDownloading(true);
    try {
      const serverClient = await getAuthenticatedServerClient();
      const pdf = (await serverClient.downloadAttachment({
        params: { attachmentId: file.id },
        responseType: "arraybuffer",
      })) as unknown as ArrayBuffer;
      const url = URL.createObjectURL(new Blob([pdf], { type: file.mimeType }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      handleError("Fehler beim Herunterladen des Formulars.", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <LoadingButton
      type="button"
      variant="outlined"
      startIcon={<CloudDownloadIcon />}
      loading={isDownloading}
      onClick={downloadForm}
    >
      Formular herunterladen
    </LoadingButton>
  );
}

function _FormInputFields({ record }: { record?: DrivingLicenseAuthorityForm }) {
  const { getValues, setValue } = useFormContext();
  const {
    field: { onChange, onBlur, value: file },
  } = useInput({ source: "pdf" });
  const onDrop = (newFiles: File[]) => {
    onChange(newFiles[0]);
    onBlur();
  };
  const pdfTemplateBaseFile = record?.pdfTemplateBaseFile;
  const fileName = file?.name ?? pdfTemplateBaseFile?.name;
  const fileSize = file?.size ?? pdfTemplateBaseFile?.size;
  return (
    <>
      <SelectInput
        label="Art"
        source="type"
        choices={_typeChoices}
        validate={required("Bitte wähle die Art des Formulars aus.")}
        fullWidth
        onChange={(event) => {
          if (event.target.value === "FUEHRERSCHEINANTRAG" && !getValues("name")) {
            setValue("name", "Führerscheinantrag");
          }
        }}
      />
      <SelectArrayInput
        label="Klassen"
        source="drivingLicenseClasses"
        choices={_licenseClassesChoices}
        options={{
          displayEmpty: true,
          renderValue: (licenseClasses) => {
            // TODO: Replace with emptyText={<i>Alle Klassen</i>} once
            //       https://github.com/marmelab/react-admin/issues/10454 is fixed.
            // Copied and adapted from SelectArrayInput.tsx ...
            const selected = Array.isArray(licenseClasses) ? licenseClasses : [];
            return (
              <div className={SelectArrayInputClasses.chips}>
                {selected.length === 0 && <i>Alle Klassen</i>}
                {selected.map((licenseClass) => {
                  return (
                    <Chip
                      key={licenseClass}
                      label={licenseClass}
                      className={SelectArrayInputClasses.chip}
                      size="small"
                    />
                  );
                })}
              </div>
            );
          },
        }}
        fullWidth
      />
      <TextInput label="Name" source="name" validate={required("Bitte gib den Namen des Formulars ein.")} fullWidth />
      {!pdfTemplateBaseFile && <UrlInput label="Link" source="url" fullWidth />}
      <FileInput
        label="PDF-Datei"
        source="pdf"
        fullWidth
        accept={"application/pdf"}
        options={{ multiple: false, onDrop }}
        placeholder={
          <>
            {!file && !pdfTemplateBaseFile && (
              <Box component="p" sx={{ color: "text.disabled" }}>
                PDF Datei hier hineinziehen oder
                <br />
                hier klicken, um PDF Datei auszuwählen ...
              </Box>
            )}
            {fileName && fileSize && (
              <p
                style={{
                  textAlign: "left",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName} ({formatFileSize(fileSize)})
              </p>
            )}
          </>
        }
      />
    </>
  );
}

const _typeLabels: Record<CreateDrivingLicenseAuthorityFormDto["type"], string> = {
  FUEHRERSCHEINANTRAG: "Führerscheinantrag",
  BF17_ANTRAG: "Antrag für BF17 (Fahrschüler)",
  BF17_BEGLEITPERSON_ANTRAG: "Antrag für BF17 (Begleitperson)",
  SONSTIGES: "Sonstiges",
};

const _typeChoices: Array<{ id: string; name: string }> = Object.entries(_typeLabels).map(([id, name]) => ({
  id,
  name,
}));

const _licenseClassesChoices = schemas.DrivingLicenseClassEnum.options.map((it) => ({ id: it, name: it }));
